import { COLORS, COLOR_ORDER, FACE_ORDER, STICKERS_PER_FACE } from './core/cube-config.js';
import { CubeState } from './core/cube-state.js';
import { CubeState4x4 } from './core/cube-state-4x4.js';
import { applyAlgorithmToState, moveDescription } from './core/moves.js';
import { moveDescription4x4 } from './core/moves-4x4.js';
import {
  clearSavedCubeInputs,
  clearCubeState4x4,
  clearOrientation4x4,
  deleteAllProjectLocalData,
  loadInputPersistencePreference,
  loadPersistedCubeInput,
  loadOrientation4x4,
  loadPlaybackSpeed,
  saveCubeState,
  saveCubeState2x2,
  saveCubeState4x4,
  saveInputPersistencePreference,
  savePersistedCubeInput,
  saveLastOrder,
  saveOrientation4x4,
  savePlaybackSpeed,
} from './core/storage.js';
import { ThemeManager } from './core/theme.js';
import {
  loadDefaultScrambleLength,
  saveDefaultScrambleLength,
} from './scramble/scramble-preferences.js';
import {
  defaultOrientation4x4,
  deriveOrientation4x4,
  orientationInstruction4x4,
  validateOrientation4x4,
} from './core/orientation-4x4.js';
import {
  createDiagnosticData4x4,
  exportCubeStateJson4x4,
  importCubeStateJson4x4,
} from './core/state-transfer-4x4.js';
import {
  createDiagnosticData3x3,
  exportCubeStateJson3x3,
  importCubeStateJson3x3,
} from './core/state-transfer-3x3.js';
import { isSolved4x4, validateCubeState4x4 } from './core/validation-4x4.js';
import { validateCubeState } from './core/validation.js';
import { validateCubeState2x2 } from './core/validation-2x2.js';
import { PlaybackSession } from './playback/playback-session.js';
import { ScrambleGeneratorPage } from './scramble/scramble-generator-page.js';
import { generateScramble2x2 } from './scramble/scramble-generator.js';
import { scramblePlaybackTemplate } from './scramble/scramble-playback-template.js';
import { SOLVER_STATUS_4X4, SOLVER_STATUS_TEXT_4X4, SolverClient4x4 } from './solver/solver-client-4x4.js';
import {
  createSolverDiagnostic4x4,
  formatSolverDiagnostic4x4,
  SOLVER_DIAGNOSTIC_STAGE_4X4,
} from './solver/solver-diagnostics-4x4.js';
import { SOLVER_STATUS, SOLVER_STATUS_TEXT, SolverClient } from './solver/solver-client.js';
import { SolverClient2x2 } from './solver/solver-client-2x2.js';
import { PracticeTimerPage } from './timer/practice-timer-page.js';
import { TimerHistoryDetailPage } from './timer/timer-history-detail-page.js';
import { TutorialPage } from './tutorial/tutorial-page.js';
import {
  appShellTemplate,
  entryTemplate,
  entryTemplate4x4,
  landingTemplate,
  orientationTemplate4x4,
  playbackTemplate,
} from './ui/templates.js';
import { CubeView4x4 } from './view/cube-view-4x4.js';
import { CubeView } from './view/cube-view.js';

const MOBILE_TOAST_DURATION_MS = 1000;
const DESKTOP_TOAST_DURATION_MS = 2400;

async function readServiceWorkerBuildInfo() {
  const controller = navigator.serviceWorker?.controller;
  if (!controller) return null;
  return new Promise((resolve) => {
    const channel = new MessageChannel();
    const timer = window.setTimeout(() => resolve(null), 750);
    channel.port1.onmessage = (event) => {
      window.clearTimeout(timer);
      resolve(event.data ?? null);
    };
    controller.postMessage({ type: 'cube-path:build-info' }, [channel.port2]);
  });
}

async function runtimeDiagnostics4x4() {
  const registration = 'serviceWorker' in navigator ? await navigator.serviceWorker.getRegistration() : null;
  const cacheKeys = globalThis.caches ? await caches.keys() : [];
  return {
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    vendor: navigator.vendor,
    languages: navigator.languages,
    maxTouchPoints: navigator.maxTouchPoints,
    workerSupported: typeof Worker === 'function',
    moduleWorkerExpected: true,
    locationHref: location.href,
    origin: location.origin,
    crossOriginIsolated: globalThis.crossOriginIsolated ?? false,
    mode: import.meta.env.MODE,
    production: import.meta.env.PROD,
    baseUrl: import.meta.env.BASE_URL,
    documentBaseUri: document.baseURI,
    serviceWorkerController: navigator.serviceWorker?.controller?.scriptURL ?? null,
    serviceWorkerActive: registration?.active?.scriptURL ?? null,
    serviceWorkerWaiting: registration?.waiting?.scriptURL ?? null,
    serviceWorkerBuild: await readServiceWorkerBuildInfo(),
    cacheKeys,
  };
}

async function copyText(text) {
  if (navigator.clipboard?.writeText) {
    try {
      await Promise.race([
        navigator.clipboard.writeText(text),
        new Promise((_, reject) => window.setTimeout(() => reject(new Error('clipboard-timeout')), 800)),
      ]);
      return;
    } catch {
      // file://、非安全上下文或权限设置可能阻止异步剪贴板，继续使用本地回退。
    }
  }
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.append(textarea);
  textarea.select();
  const copied = document.execCommand('copy');
  textarea.remove();
  if (!copied) throw new Error('浏览器拒绝写入剪贴板');
}

function exportTimestamp(date = new Date()) {
  const pad = (value) => String(value).padStart(2, '0');
  return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}-${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`;
}

function downloadJson(data, filename) {
  const blob = new Blob([`${JSON.stringify(data, null, 2)}\n`], { type: 'application/json;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export class CubeEntryApp {
  constructor(root) {
    this.themeManager = new ThemeManager({ onChange: ({ resolved }) => this.#updateThemeMeta(resolved) });
    this.inputPersistenceEnabled = loadInputPersistencePreference();
    this.defaultScrambleLength = loadDefaultScrambleLength();
    this.root = root;
    this.root.innerHTML = appShellTemplate();
    this.screen = root.querySelector('#screen');
    this.liveRegion = root.querySelector('#live-region');
    this.toast = root.querySelector('[data-toast]');
    this.selectedColor = 'white';
    this.currentOrder = 3;
    this.cubeView = null;
    this.state = null;
    this.orientation4x4 = this.inputPersistenceEnabled ? loadOrientation4x4() : null;
    this.sessionStates = { 2: null, 3: null, 4: null };
    this.solver2x2 = new SolverClient2x2();
    this.solver3x3 = new SolverClient();
    this.solver4x4 = new SolverClient4x4({ solver3x3: this.solver3x3 });
    this.solution = null;
    this.playback = null;
    this.playbackMode = null;
    this.scramble = null;
    this.solveInProgress = false;
    this.solveDiagnostics4x4 = [];
    this.timerPage = null;
    this.tutorialPage = null;

    root.addEventListener('click', (event) => this.#handleClick(event));
    root.addEventListener('change', (event) => this.#handleChange(event));
    root.addEventListener('focusout', (event) => {
      if (event.target.matches('[data-default-scramble-length]')) this.#commitDefaultScrambleLength(event.target);
    });
    root.querySelector('[data-persistence-dialog]')?.addEventListener('close', () => this.#syncSettingsControls());
    window.addEventListener('hashchange', () => this.route());
    this.#syncSettingsControls();
    this.route();
  }

  route() {
    const route = window.location.hash.replace(/^#\/?/, '');
    const timerHistoryMatch = /^timer\/history\/(.+)$/.exec(route);
    if (route === 'input/2') this.showEntry(2);
    else if (route === 'input/3') this.showEntry(3);
    else if (route === 'orientation/4') this.showOrientation4x4();
    else if (route === 'input/4') this.showEntry(4);
    else if (timerHistoryMatch) this.showTimerHistoryDetail(timerHistoryMatch[1]);
    else if (route === 'timer/scramble/playback') this.showScramblePlayback();
    else if (route === 'timer/scramble') this.showScrambleGenerator();
    else if (route === 'timer/history') this.showTimer({ scrollToHistory: true });
    else if (route === 'timer') this.showTimer();
    else if (route === 'tutorial') this.showTutorial();
    else if (/^playback\/(2|3|4)$/.test(route) && this.solution?.order === Number(route.at(-1))) this.showPlayback();
    else if (route.startsWith('playback')) window.location.hash = `#/input/${this.solution?.order ?? 3}`;
    else this.showLanding();
  }

  #disposeCube() {
    if (this.playback) this.playback.playing = false;
    this.timerPage?.dispose();
    this.timerPage = null;
    this.tutorialPage?.dispose();
    this.tutorialPage = null;
    this.cubeView?.dispose();
    this.cubeView = null;
  }

  showLanding() {
    this.#disposeCube();
    this.currentOrder = 3;
    this.screen.innerHTML = landingTemplate();
    this.cubeView = new CubeView(this.screen.querySelector('[data-cube-canvas]'));
    this.cubeView.setStickers(CubeState.solved().stickers);
    document.title = '复原轨迹 · 3D 魔方还原';
  }

  showOrientation4x4() {
    this.#disposeCube();
    this.currentOrder = 4;
    const initial = this.orientation4x4 ?? defaultOrientation4x4();
    this.screen.innerHTML = orientationTemplate4x4(initial);
    this.#refreshOrientationSetup();
    document.title = '4×4 方向配置 · 复原轨迹';
  }

  showTimer({ scrollToHistory = false } = {}) {
    this.#disposeCube();
    const timerPage = new PracticeTimerPage(this.screen, {
      announce: (message) => this.#announce(message),
    });
    this.timerPage = timerPage;
    if (scrollToHistory) requestAnimationFrame(() => timerPage.scrollToHistory());
    document.title = '练习计时器 · 复原轨迹';
  }

  showTutorial() {
    this.#disposeCube();
    this.tutorialPage = new TutorialPage(this.screen);
    document.title = '魔方教程 · 复原轨迹';
  }

  showTimerHistoryDetail(encodedId) {
    this.#disposeCube();
    let recordId = encodedId;
    try {
      recordId = decodeURIComponent(encodedId);
    } catch {
      // 无效编码按不存在的记录处理，详情页会提供安全返回入口。
    }
    this.timerPage = new TimerHistoryDetailPage(this.screen, recordId, {
      announce: (message) => this.#announce(message),
    });
    document.title = '计时历史详情 · 复原轨迹';
  }

  showScrambleGenerator() {
    this.#disposeCube();
    this.playbackMode = null;
    this.timerPage = new ScrambleGeneratorPage(this.screen, {
      announce: (message) => this.#announce(message),
      defaultLength: this.defaultScrambleLength,
      onGenerate: (scramble) => {
        this.scramble = scramble;
        window.location.hash = '#/timer/scramble/playback';
      },
    });
    document.title = '打乱公式生成 · 复原轨迹';
  }

  showScramblePlayback() {
    if (!this.scramble) {
      window.location.hash = '#/timer/scramble';
      return;
    }
    this.#disposeCube();
    this.currentOrder = 3;
    this.playbackMode = 'scramble';
    const playbackSpeed = loadPlaybackSpeed();
    this.playback = new PlaybackSession({
      initialStickers: CubeState.solved().stickers,
      moves: this.scramble.moves,
      formula: this.scramble.formula,
      order: 3,
      speed: playbackSpeed,
    });
    this.screen.innerHTML = scramblePlaybackTemplate(this.scramble, playbackSpeed);
    this.cubeView = new CubeView(this.screen.querySelector('[data-cube-canvas]'));
    this.cubeView.setStickers(this.playback.currentStickers);
    this.#refreshPlayback();
    document.title = '3×3 打乱公式执行 · 复原轨迹';
  }

  showEntry(order = 3, { state = null } = {}) {
    this.#disposeCube();
    this.currentOrder = order;
    saveLastOrder(order);
    if (order === 4) {
      this.orientation4x4 = this.orientation4x4
        ?? (this.inputPersistenceEnabled ? loadOrientation4x4() : null);
      if (!this.orientation4x4) {
        window.location.hash = '#/orientation/4';
        return;
      }
      const saved = !state && !this.sessionStates[4] && this.inputPersistenceEnabled
        ? loadPersistedCubeInput(4, {
          enabled: this.inputPersistenceEnabled,
          orientation: this.orientation4x4,
        })
        : null;
      this.state = state ?? this.sessionStates[4] ?? saved ?? new CubeState4x4({ orientation: this.orientation4x4 });
      this.sessionStates[4] = this.state;
      this.screen.innerHTML = entryTemplate4x4(this.selectedColor, this.orientation4x4, {
        persistenceEnabled: this.inputPersistenceEnabled,
      });
      this.cubeView = new CubeView4x4(this.screen.querySelector('[data-cube-canvas]'), {
        interactive: true,
        onStickerSelect: ({ face, index }) => this.#paintSticker(face, index),
      });
      this.#refreshEntry();
      if (saved) this.#announce('已恢复上次保存在此浏览器的 4×4 录入进度');
      document.title = '4×4 颜色录入 · 复原轨迹';
      return;
    }

    const saved = !state && !this.sessionStates[order]
      ? loadPersistedCubeInput(order, { enabled: this.inputPersistenceEnabled })
      : null;
    this.state = state ?? this.sessionStates[order] ?? saved ?? new CubeState({ order });
    this.sessionStates[order] = this.state;
    this.screen.innerHTML = entryTemplate(this.selectedColor, { persistenceEnabled: this.inputPersistenceEnabled, order });
    this.cubeView = new CubeView(this.screen.querySelector('[data-cube-canvas]'), {
      order,
      interactive: true,
      onStickerSelect: ({ face, index }) => this.#paintSticker(face, index),
    });
    this.#refreshEntry();
    if (saved) this.#announce(`已恢复上次保存在此浏览器的 ${order}×${order} 录入进度`);
    document.title = `${order}×${order} 颜色录入 · 复原轨迹`;
  }

  showPlayback() {
    this.#disposeCube();
    this.currentOrder = this.solution.order;
    this.playbackMode = 'solution';
    const playbackSpeed = loadPlaybackSpeed();
    this.solution.playbackSpeed = playbackSpeed;
    this.playback = new PlaybackSession({
      initialStickers: this.solution.initialStickers,
      moves: this.solution.moves,
      formula: this.solution.formula,
      durationMs: this.solution.durationMs,
      order: this.solution.order,
      speed: playbackSpeed,
    });
    this.screen.innerHTML = playbackTemplate(this.solution);
    this.cubeView = this.solution.order === 4
      ? new CubeView4x4(this.screen.querySelector('[data-cube-canvas]'))
      : new CubeView(this.screen.querySelector('[data-cube-canvas]'), { order: this.solution.order });
    this.cubeView.setStickers(this.playback.currentStickers);
    this.#refreshPlayback();
    document.title = `${this.solution.order}×${this.solution.order} 还原演示 · 复原轨迹`;
  }

  #handleClick(event) {
    const colorButton = event.target.closest('[data-color]');
    if (colorButton) {
      this.selectedColor = colorButton.dataset.color;
      this.#refreshPalette();
      return;
    }
    const actionButton = event.target.closest('[data-action]');
    if (!actionButton) return;
    const { action } = actionButton.dataset;
    if (action === 'open-order-picker') this.screen.querySelector('[data-order-dialog]').showModal();
    else if (action === 'open-timer') window.location.hash = '#/timer';
    else if (action === 'open-tutorial') window.location.hash = '#/tutorial';
    else if (action === 'open-scramble') window.location.hash = '#/timer/scramble';
    else if (action === 'back-scramble-settings') window.location.hash = '#/timer/scramble';
    else if (action === 'back-timer') window.location.hash = '#/timer';
    else if (action === 'start-2x2') {
      this.screen.querySelector('[data-order-dialog]')?.close();
      window.location.hash = '#/input/2';
    } else if (action === 'start-3x3') {
      this.screen.querySelector('[data-order-dialog]')?.close();
      window.location.hash = '#/input/3';
    } else if (action === 'start-4x4') {
      this.screen.querySelector('[data-order-dialog]')?.close();
      const orientation = this.orientation4x4 ?? (this.inputPersistenceEnabled ? loadOrientation4x4() : null);
      window.location.hash = orientation ? '#/input/4' : '#/orientation/4';
    } else if (action === 'confirm-4x4-orientation') this.#confirmOrientation4x4();
    else if (action === 'change-4x4-orientation') this.#changeOrientation4x4();
    else if (action === 'home') {
      if (this.solveInProgress) this.solver4x4.cancel('已离开求解页面');
      window.location.hash = '#/';
      if (window.location.hash === '#/') this.route();
    } else if (action === 'undo') {
      if (this.state?.undo()) this.#persistAndRefresh('已撤销上一次操作');
    } else if (action === 'reset') {
      if (this.state?.reset()) this.#persistAndRefresh('已重置当前阶数的贴纸，可点击撤销恢复');
      else this.#announce('当前已经是初始录入状态');
    } else if (action === 'rescramble-2x2') {
      this.#rescramble2x2();
    } else if (action === 'save') {
      if (!this.inputPersistenceEnabled) {
        this.root.querySelector('[data-settings-dialog]')?.showModal();
        this.#announce('请先在设置中开启“保存魔方输入数据”');
        return;
      }
      this.#saveCurrentState();
      this.#setSaveStatus('刚刚已保存到本机');
      this.#announce(`${this.currentOrder}×${this.currentOrder} 录入进度已保存到此浏览器`);
    } else if (action === 'copy-diagnostics') void this.#copyDiagnostics4x4();
    else if (action === 'copy-diagnostics-3x3') void this.#copyDiagnostics3x3();
    else if (action === 'export-3x3-json') this.#exportStateJson3x3();
    else if (action === 'import-3x3-json') this.screen.querySelector('[data-import-3x3-json]')?.click();
    else if (action === 'export-4x4-json') this.#exportStateJson4x4();
    else if (action === 'import-4x4-json') this.screen.querySelector('[data-import-4x4-json]')?.click();
    else if (action === 'open-settings') this.root.querySelector('[data-settings-dialog]')?.showModal();
    else if (action === 'close-settings') this.root.querySelector('[data-settings-dialog]')?.close();
    else if (action === 'cancel-disable-persistence') this.#cancelDisablePersistence();
    else if (action === 'disable-persistence-keep') this.#disableInputPersistence(false);
    else if (action === 'disable-persistence-delete') this.#disableInputPersistence(true);
    else if (action === 'request-delete-data') this.root.querySelector('[data-delete-dialog]')?.showModal();
    else if (action === 'cancel-delete-data') this.root.querySelector('[data-delete-dialog]')?.close();
    else if (action === 'confirm-delete-data') void this.#deleteAllLocalData();
    else if (action === 'solve') void this.#solveCurrentState();
    else if (action === 'cancel-solve') this.solver4x4.cancel();
    else if (action === 'back-edit') window.location.hash = `#/input/${this.solution?.order ?? this.currentOrder}`;
    else if (action === 'next') void this.#goToAdjacentStep(1);
    else if (action === 'previous') void this.#goToAdjacentStep(-1);
    else if (action === 'play') void this.#startAutoPlay();
    else if (action === 'pause') this.#pausePlayback();
    else if (action === 'restart' || action === 'first') this.#jumpToStep(0);
    else if (action === 'last') this.#jumpToStep(this.playback.totalSteps);
  }

  #handleChange(event) {
    if (event.target.matches('[data-import-3x3-json]')) {
      void this.#importStateJson3x3(event.target);
      return;
    }
    if (event.target.matches('[data-import-4x4-json]')) {
      void this.#importStateJson4x4(event.target);
      return;
    }
    if (event.target.matches('[data-theme-select]')) {
      this.themeManager.setPreference(event.target.value);
      this.#announce(`主题已切换为${event.target.selectedOptions[0].textContent}`);
      return;
    }
    if (event.target.matches('[data-input-persistence]')) {
      if (event.target.checked) this.#enableInputPersistence();
      else this.root.querySelector('[data-persistence-dialog]')?.showModal();
      return;
    }
    if (event.target.matches('[data-default-scramble-length]')) {
      this.#commitDefaultScrambleLength(event.target);
      return;
    }
    if (event.target.matches('[data-orientation-top], [data-orientation-front]')) {
      this.#refreshOrientationSetup();
      return;
    }
    const select = event.target.closest('[data-playback-speed]');
    if (!select || !this.playback) return;
    this.playback.speed = Number(select.value);
    savePlaybackSpeed(this.playback.speed);
    this.#announce(`播放速度已调整为 ${select.value} 倍`);
  }

  #commitDefaultScrambleLength(input) {
    const rawValue = input.value;
    if (String(rawValue).trim() === '' || !Number.isFinite(Number(rawValue))) {
      input.value = String(this.defaultScrambleLength);
      this.#announce('请输入 2～50 之间的有效数字');
      return;
    }
    const normalized = saveDefaultScrambleLength(rawValue);
    input.value = String(normalized);
    if (normalized === this.defaultScrambleLength) return;
    this.defaultScrambleLength = normalized;
    this.#announce(`默认打乱次数已设置为 ${this.defaultScrambleLength}`);
  }

  #updateThemeMeta(resolved) {
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', resolved === 'dark' ? '#09101f' : '#f3f5f9');
  }

  #syncSettingsControls() {
    const themeSelect = this.root?.querySelector('[data-theme-select]');
    const persistenceSwitch = this.root?.querySelector('[data-input-persistence]');
    const defaultScrambleLengthInput = this.root?.querySelector('[data-default-scramble-length]');
    if (themeSelect) themeSelect.value = this.themeManager.preference;
    if (persistenceSwitch) persistenceSwitch.checked = this.inputPersistenceEnabled;
    if (defaultScrambleLengthInput) defaultScrambleLengthInput.value = String(this.defaultScrambleLength);
  }

  #refreshPersistenceUi() {
    this.#syncSettingsControls();
    const saveButton = this.screen.querySelector('[data-action="save"]');
    if (saveButton) saveButton.innerHTML = `<span aria-hidden="true">✓</span> ${this.inputPersistenceEnabled ? '立即保存到本机' : '开启输入保存'}`;
    this.#setSaveStatus(this.inputPersistenceEnabled
      ? '当前进度会自动保存在此浏览器'
      : '仅保留在当前页面会话，未写入本机');
  }

  #enableInputPersistence() {
    this.inputPersistenceEnabled = saveInputPersistencePreference(true);
    if (this.sessionStates[2]) saveCubeState2x2(this.sessionStates[2]);
    if (this.sessionStates[3]) saveCubeState(this.sessionStates[3]);
    if (this.sessionStates[4]) saveCubeState4x4(this.sessionStates[4]);
    if (this.orientation4x4) saveOrientation4x4(this.orientation4x4);
    this.#refreshPersistenceUi();
    this.#announce('已开启魔方输入保存，2×2、3×3和4×4进度将分别保存在此浏览器');
  }

  #cancelDisablePersistence() {
    this.root.querySelector('[data-persistence-dialog]')?.close();
    this.#syncSettingsControls();
  }

  #disableInputPersistence(deleteSavedData) {
    if (deleteSavedData) clearSavedCubeInputs();
    this.inputPersistenceEnabled = saveInputPersistencePreference(false);
    this.root.querySelector('[data-persistence-dialog]')?.close();
    this.#refreshPersistenceUi();
    this.#announce(deleteSavedData ? '输入保存已关闭，已保存的魔方输入也已删除' : '输入保存已关闭，已有数据已保留但不会自动恢复');
  }

  async #deleteAllLocalData() {
    const confirmButton = this.root.querySelector('[data-action="confirm-delete-data"]');
    if (confirmButton) confirmButton.disabled = true;
    try {
      await deleteAllProjectLocalData();
      this.inputPersistenceEnabled = false;
      this.themeManager.reset();
      this.defaultScrambleLength = loadDefaultScrambleLength();
      this.sessionStates = { 2: null, 3: null, 4: null };
      this.orientation4x4 = null;
      this.state = null;
      this.solution = null;
      this.playback = null;
      this.playbackMode = null;
      this.scramble = null;
      this.solveDiagnostics4x4 = [];
      this.root.querySelector('[data-delete-dialog]')?.close();
      this.root.querySelector('[data-settings-dialog]')?.close();
      this.#syncSettingsControls();
      window.location.hash = '#/';
      this.route();
      this.#announce('本地数据已删除');
    } catch (cause) {
      this.#announce(cause.message);
    } finally {
      if (confirmButton) confirmButton.disabled = false;
    }
  }

  #refreshOrientationSetup() {
    const top = this.screen.querySelector('[data-orientation-top]')?.value;
    const front = this.screen.querySelector('[data-orientation-front]')?.value;
    const errors = validateOrientation4x4({ top, front });
    const errorPanel = this.screen.querySelector('[data-orientation-errors]');
    const preview = this.screen.querySelector('[data-orientation-preview]');
    const button = this.screen.querySelector('[data-action="confirm-4x4-orientation"]');
    button.disabled = errors.length > 0;
    errorPanel.hidden = errors.length === 0;
    errorPanel.innerHTML = errors.length ? `<strong>方向不可用</strong><ul>${errors.map((item) => `<li>${item.message}</li>`).join('')}</ul>` : '';
    if (errors.length) {
      preview.textContent = '请选择两个相邻面的颜色';
      return;
    }
    const orientation = deriveOrientation4x4({ top, front });
    preview.innerHTML = `<strong>${orientationInstruction4x4(orientation)}</strong><div>${FACE_ORDER.map((face) => {
      const color = orientation.faceToColor[face];
      return `<span><i style="--dot:${COLORS[color].hex}"></i>${face} · ${COLORS[color].name}</span>`;
    }).join('')}</div>`;
  }

  #confirmOrientation4x4() {
    const top = this.screen.querySelector('[data-orientation-top]').value;
    const front = this.screen.querySelector('[data-orientation-front]').value;
    const errors = validateOrientation4x4({ top, front });
    if (errors.length) return this.#announce(errors[0].message);
    this.orientation4x4 = deriveOrientation4x4({ top, front });
    this.sessionStates[4] = null;
    if (this.inputPersistenceEnabled) saveOrientation4x4(this.orientation4x4);
    saveLastOrder(4);
    window.location.hash = '#/input/4';
  }

  #changeOrientation4x4() {
    if (this.state?.filledCount > 0) {
      const confirmed = window.confirm('重新选择方向会清空当前 4×4 录入，但不会影响 3×3 数据。是否继续？');
      if (!confirmed) return;
    }
    if (this.inputPersistenceEnabled) {
      clearCubeState4x4();
      clearOrientation4x4();
    }
    this.sessionStates[4] = null;
    this.orientation4x4 = null;
    window.location.hash = '#/orientation/4';
  }

  #paintSticker(face, index) {
    if (this.state.isLocked(face, index)) {
      this.#announce(`${face} 面中心块固定，不可修改`);
      return;
    }
    const result = this.state.paint(face, index, this.selectedColor);
    const maximum = this.currentOrder * this.currentOrder;
    if (result.reason === 'color-full') {
      this.#announce(`${COLORS[this.selectedColor].name}已经填满 ${maximum} 张，请选择其他颜色`);
      return;
    }
    if (result.changed) this.#persistAndRefresh(`${face}${index} 已填写为${COLORS[this.selectedColor].name}`);
  }

  #rescramble2x2() {
    if (this.currentOrder !== 2) return;
    const scramble = generateScramble2x2();
    this.state = new CubeState({
      order: 2,
      stickers: applyAlgorithmToState(CubeState.solved(2).stickers, scramble.moves),
    });
    this.sessionStates[2] = this.state;
    this.#persistAndRefresh(`已生成新的 2×2 打乱：${scramble.formula}`);
  }

  #saveCurrentState() {
    if (!this.state) return false;
    return savePersistedCubeInput(this.currentOrder, this.state, {
      enabled: this.inputPersistenceEnabled,
      orientation: this.orientation4x4,
    });
  }

  async #copyDiagnostics3x3() {
    if (this.currentOrder !== 3 || !this.state) return;
    try {
      await copyText(JSON.stringify({
        ...createDiagnosticData3x3(this.state.stickers),
        solveDiagnostics: this.solveDiagnostics4x4,
      }, null, 2));
      this.#announce('3×3诊断数据已复制到剪贴板');
    } catch (cause) {
      this.#showValidationErrors([{ message: `复制失败：${cause.message}` }], '诊断数据未复制');
      this.#announce(`复制失败：${cause.message}`);
    }
  }

  async #copyDiagnostics4x4() {
    if (this.currentOrder !== 4 || !this.state || !this.orientation4x4) return;
    try {
      const data = createDiagnosticData4x4(this.state.stickers, this.orientation4x4, this.solveDiagnostics4x4);
      await copyText(JSON.stringify(data, null, 2));
      this.#announce('4×4诊断数据已复制到剪贴板');
    } catch (cause) {
      this.#showValidationErrors([{ message: `复制失败：${cause.message}` }], '诊断数据未复制');
      this.#announce(`复制失败：${cause.message}`);
    }
  }

  #exportStateJson3x3() {
    if (this.currentOrder !== 3 || !this.state) return;
    try {
      const data = exportCubeStateJson3x3(this.state.stickers);
      downloadJson(data, `cube-3x3-state-${exportTimestamp()}.json`);
      this.#announce('当前3×3状态JSON已导出');
    } catch (cause) {
      this.#showValidationErrors([{ message: `导出失败：${cause.message}` }], '状态未导出');
      this.#announce(`导出失败：${cause.message}`);
    }
  }

  #exportStateJson4x4() {
    if (this.currentOrder !== 4 || !this.state || !this.orientation4x4) return;
    try {
      const data = exportCubeStateJson4x4(this.state.stickers, this.orientation4x4);
      downloadJson(data, `cube-4x4-state-${exportTimestamp()}.json`);
      this.#announce('当前4×4状态JSON已导出');
    } catch (cause) {
      this.#showValidationErrors([{ message: `导出失败：${cause.message}` }], '状态未导出');
      this.#announce(`导出失败：${cause.message}`);
    }
  }

  async #importStateJson3x3(input) {
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    try {
      const imported = importCubeStateJson3x3(await file.text());
      this.sessionStates[3] = imported.state;
      if (this.inputPersistenceEnabled) saveCubeState(imported.state);
      this.showEntry(3, { state: imported.state });
      this.#announce(`3×3状态已从JSON导入${this.inputPersistenceEnabled ? '并保存到本机' : '；仅用于当前会话'}`);
    } catch (cause) {
      this.#showValidationErrors([{ message: cause.message }], 'JSON导入失败');
      this.#announce(`JSON导入失败：${cause.message}`);
    }
  }

  async #importStateJson4x4(input) {
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    try {
      const imported = importCubeStateJson4x4(await file.text());
      if (imported.state.filledCount === 96) {
        const formalValidation = validateCubeState4x4(imported.state.stickers, imported.orientation);
        if (!formalValidation.valid) throw new TypeError(`完整状态未通过正式校验：${formalValidation.errors[0].message}`);
      }
      // 只有全部冗余字段与正式校验通过后，才根据用户设置决定是否持久化。
      this.orientation4x4 = imported.orientation;
      this.state = imported.state;
      this.sessionStates[4] = imported.state;
      if (this.inputPersistenceEnabled) {
        saveOrientation4x4(imported.orientation);
        saveCubeState4x4(imported.state);
      }
      this.solveDiagnostics4x4 = [];
      this.showEntry(4, { state: imported.state });
      this.#announce(`4×4状态已从JSON导入${this.inputPersistenceEnabled ? '并保存到本机' : '；仅用于当前会话'}；3×3数据未改动`);
    } catch (cause) {
      this.#showValidationErrors([{ message: cause.message }], 'JSON导入失败');
      this.#announce(`JSON导入失败：${cause.message}`);
    }
  }

  #persistAndRefresh(message) {
    const saved = this.#saveCurrentState();
    this.#refreshEntry();
    this.#setSaveStatus(saved ? '更改已自动保存' : '更改仅保留在当前页面会话');
    this.#clearValidationErrors();
    this.#announce(message);
  }

  #refreshEntry() {
    if (!this.state || !this.cubeView) return;
    this.cubeView.setStickers(this.state.stickers);
    this.#refreshPalette();
    const order4 = this.currentOrder === 4;
    const counts = this.state.countColors();
    const filled = this.currentOrder === 3 ? Math.max(this.state.filledCount - 6, 0) : this.state.filledCount;
    const total = this.currentOrder === 4 ? 96 : this.currentOrder === 2 ? 24 : 48;
    const perColor = order4 ? 16 : this.currentOrder === 2 ? 4 : STICKERS_PER_FACE;
    const percentage = Math.round((filled / total) * 100);
    this.screen.querySelector('[data-filled-count]').textContent = String(filled);
    this.screen.querySelector('[data-progress-percent]').textContent = `${percentage}%`;
    this.screen.querySelector('[data-progress-bar]').style.width = `${percentage}%`;
    this.screen.querySelector('[data-progress-ring]').style.setProperty('--progress', `${percentage * 3.6}deg`);
    this.screen.querySelector('[data-action="undo"]').disabled = !this.state.canUndo;
    this.screen.querySelector('[data-color-counts]').innerHTML = COLOR_ORDER.map((color) => {
      const count = counts[color];
      return `<div class="count-row"><span class="count-color"><i style="--dot:${COLORS[color].hex}"></i>${COLORS[color].name}</span><strong>${count}<small> / ${perColor}</small></strong><span class="remaining">剩 ${perColor - count}</span></div>`;
    }).join('');
  }

  async #solveCurrentState() {
    const button = this.screen.querySelector('[data-action="solve"]');
    if (button.disabled) return;
    const solveStartedAtMs = Date.now();
    this.solveDiagnostics4x4 = [];
    this.#recordSolverDiagnostic4x4(createSolverDiagnostic4x4(
      SOLVER_DIAGNOSTIC_STAGE_4X4.UI_START,
      { source: 'ui', startedAtMs: solveStartedAtMs, details: { order: this.currentOrder } },
    ));
    void runtimeDiagnostics4x4()
      .then((details) => this.#recordSolverDiagnostic4x4(createSolverDiagnostic4x4(
        SOLVER_DIAGNOSTIC_STAGE_4X4.RUNTIME_ENVIRONMENT,
        { source: 'ui', startedAtMs: solveStartedAtMs, details },
      )))
      .catch((cause) => this.#recordSolverDiagnostic4x4(createSolverDiagnostic4x4(
        SOLVER_DIAGNOSTIC_STAGE_4X4.RUNTIME_ENVIRONMENT,
        { source: 'ui', startedAtMs: solveStartedAtMs, details: { error: cause?.message ?? String(cause) } },
      )));
    button.disabled = true;
    this.solveInProgress = true;
    this.#clearValidationErrors();
    this.#setSolverStatus(this.currentOrder === 4 ? 'checking-colors' : SOLVER_STATUS.VALIDATING);
    await new Promise((resolve) => requestAnimationFrame(resolve));

    const validation = this.currentOrder === 4
      ? validateCubeState4x4(this.state.stickers, this.orientation4x4)
      : this.currentOrder === 2 ? validateCubeState2x2(this.state.stickers) : validateCubeState(this.state.stickers);
    if (!validation.valid) {
      this.#hideSolverStatus();
      this.#showValidationErrors(validation.errors);
      button.disabled = false;
      this.solveInProgress = false;
      this.#announce(validation.errors[0].message);
      return;
    }

    const initialStickers = structuredClone(this.state.stickers);
    try {
      const result = this.currentOrder === 4
        ? await this.solver4x4.solve(initialStickers, this.orientation4x4, {
          onStatus: (status) => this.#setSolverStatus(status),
          onDiagnostic: (event) => this.#recordSolverDiagnostic4x4(event),
          startedAtMs: solveStartedAtMs,
        })
        : this.currentOrder === 2
          ? await this.solver2x2.solve(validation.patternData, {
              onStatus: (status) => this.#setSolverStatus(status),
            })
          : await this.solver3x3.solve(validation.patternData, {
              onStatus: (status) => this.#setSolverStatus(status),
              onDiagnostic: (event) => this.#recordSolverDiagnostic4x4(event),
              startedAtMs: solveStartedAtMs,
            });
      this.solution = {
        ...result,
        order: this.currentOrder,
        initialStickers,
        orientation: this.currentOrder === 4 ? this.orientation4x4 : null,
        diagnostics: [...this.solveDiagnostics4x4],
      };
      window.location.hash = `#/playback/${this.currentOrder}`;
    } catch (solverError) {
      this.#hideSolverStatus();
      const title = solverError.code === 'CANCELED' ? '求解已取消' : '求解未完成';
      this.#showValidationErrors([{ message: solverError.message }], title);
      console.error(`[${this.currentOrder}×${this.currentOrder}求解诊断]`, solverError, {
        code: solverError.code,
        name: solverError.name,
        stage: solverError.workerStage,
        resourceUrl: solverError.resourceUrl,
        stack: solverError.workerStack || solverError.cause?.stack || solverError.stack || '',
      });
      button.disabled = false;
      this.#announce(solverError.message);
    } finally {
      this.solveInProgress = false;
    }
  }

  #setSolverStatus(status) {
    const panel = this.screen.querySelector('[data-solver-status]');
    const title = this.screen.querySelector('[data-solver-status-title]');
    if (!panel || !title) return;
    panel.hidden = false;
    if (status === 'checking-colors') title.textContent = '正在检查颜色与方向';
    else title.textContent = SOLVER_STATUS_TEXT_4X4[status] ?? SOLVER_STATUS_TEXT[status] ?? '正在处理';
  }

  #recordSolverDiagnostic4x4(event) {
    this.solveDiagnostics4x4.push(event);
    console.info(`[${this.currentOrder}×${this.currentOrder}求解诊断]`, event);
    const panel = this.screen.querySelector('[data-solver-diagnostics]');
    const log = this.screen.querySelector('[data-solver-log]');
    if (!panel || !log) return;
    panel.hidden = false;
    log.textContent = this.solveDiagnostics4x4.map(formatSolverDiagnostic4x4).join('\n');
    log.scrollTop = log.scrollHeight;
  }

  #hideSolverStatus() {
    const panel = this.screen.querySelector('[data-solver-status]');
    if (panel) panel.hidden = true;
  }

  #showValidationErrors(errors, title = '状态无法求解') {
    const panel = this.screen.querySelector('[data-validation-errors]');
    if (!panel) return;
    panel.hidden = false;
    panel.innerHTML = `<strong>${title}</strong><ul>${errors.map((item) => `<li>${item.message}</li>`).join('')}</ul>`;
  }

  #clearValidationErrors() {
    const panel = this.screen.querySelector('[data-validation-errors]');
    if (panel) {
      panel.hidden = true;
      panel.textContent = '';
    }
  }

  async #goToAdjacentStep(direction) {
    if (!this.playback || this.playback.transitioning) return;
    const target = this.playback.step + direction;
    if (target < 0 || target > this.playback.totalSteps) return;
    const transition = this.playback.prepareAdjacentStep(target);
    this.playback.transitioning = true;
    this.#refreshPlayback();
    try {
      await this.cubeView.animateMove(transition.move, {
        duration: 480 / this.playback.speed,
        finalStickers: transition.nextStickers,
      });
      this.playback.commitTransition(transition);
      this.#verifyPlaybackEnd();
    } catch (animationError) {
      this.playback.playing = false;
      this.#announce(animationError.message);
    } finally {
      this.playback.transitioning = false;
      this.#refreshPlayback();
    }
  }

  async #startAutoPlay() {
    if (!this.playback || this.playback.playing) return;
    if (this.playback.step === this.playback.totalSteps) this.#jumpToStep(0);
    this.playback.playing = true;
    this.#refreshPlayback();
    while (this.playback.playing && this.playback.step < this.playback.totalSteps) await this.#goToAdjacentStep(1);
    this.playback.playing = false;
    this.#refreshPlayback();
  }

  #pausePlayback() {
    if (!this.playback) return;
    this.playback.playing = false;
    this.#refreshPlayback();
    this.#announce('将在当前完整动作结束后暂停');
  }

  #jumpToStep(step) {
    if (!this.playback || this.playback.transitioning) return;
    this.playback.playing = false;
    this.cubeView.setStickers(this.playback.jumpTo(step));
    this.#verifyPlaybackEnd();
    this.#refreshPlayback();
  }

  #verifyPlaybackEnd() {
    if (
      this.playbackMode !== 'solution'
      || !this.playback
      || this.playback.step !== this.playback.totalSteps
      || this.solution?.order !== 4
    ) return;
    if (!isSolved4x4(this.playback.currentStickers, this.solution.orientation)) {
      this.playback.playing = false;
      this.#announce('内部验证失败：动画结束状态没有完全复原');
    }
  }

  #refreshPlayback() {
    if (!this.playback) return;
    const { step, totalSteps, currentMove, nextMove, transitioning, playing } = this.playback;
    this.screen.querySelector('[data-playback-step]').textContent = String(step);
    this.screen.querySelector('[data-current-move]').textContent = currentMove ?? (totalSteps === 0 ? '完成' : '准备');
    this.screen.querySelector('[data-next-move]').textContent = nextMove ?? '已完成';
    const describe = this.playback.order === 4 ? moveDescription4x4 : moveDescription;
    this.screen.querySelector('[data-move-description]').textContent = currentMove
      ? describe(currentMove)
      : totalSteps === 0
        ? '当前状态已经复原'
        : this.playbackMode === 'scramble' ? '从完整复原状态开始' : '从录入状态开始';
    this.screen.querySelectorAll('[data-formula-step]').forEach((token) => {
      const tokenStep = Number(token.dataset.formulaStep);
      token.classList.toggle('is-current', tokenStep === step);
      token.classList.toggle('is-complete', tokenStep <= step);
    });
    for (const action of ['previous', 'next', 'first', 'last', 'restart']) {
      const button = this.screen.querySelector(`[data-action="${action}"]`);
      if (!button) continue;
      button.disabled = transitioning
        || (['previous', 'first', 'restart'].includes(action) ? step === 0 : step === totalSteps);
    }
    this.screen.querySelector('[data-action="play"]').hidden = playing;
    this.screen.querySelector('[data-action="pause"]').hidden = !playing;
  }

  #refreshPalette() {
    this.screen.querySelectorAll('[data-color]').forEach((button) => {
      const selected = button.dataset.color === this.selectedColor;
      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-pressed', String(selected));
    });
    const label = this.screen.querySelector('[data-selected-label]');
    if (label) label.textContent = `已选${COLORS[this.selectedColor].name}`;
  }

  #setSaveStatus(message) {
    const status = this.screen.querySelector('[data-save-status]');
    if (status) status.lastChild.textContent = ` ${message}`;
  }

  #announce(message) {
    this.liveRegion.textContent = '';
    requestAnimationFrame(() => { this.liveRegion.textContent = message; });
    this.toast.textContent = message;
    this.toast.classList.add('is-visible');
    window.clearTimeout(this.toastTimer);
    const duration = window.matchMedia('(max-width: 720px)').matches
      ? MOBILE_TOAST_DURATION_MS
      : DESKTOP_TOAST_DURATION_MS;
    this.toastTimer = window.setTimeout(() => this.toast.classList.remove('is-visible'), duration);
  }
}
